const CONSENT_STORAGE_KEY = '82zone_cookie_consent_v1';
const CONSENT_FEATURE = { name: 'consent-tracking-api', version: '0.1' };

class CookieConsent extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.bar = this.querySelector('[data-cookie-consent-bar]');
    this.panel = this.querySelector('[data-cookie-consent-panel]');
    this.backdrop = this.querySelector('[data-cookie-consent-backdrop]');
    this.toggles = [...this.querySelectorAll('[data-cookie-consent-toggle]')];

    this.bindEvents();
    document.documentElement.classList.add('cookie-consent-custom-ready');
    this.loadPrivacyApi().then((api) => {
      this.privacyApi = api;
      if (this.shouldRender(api)) {
        this.show();
      }
    });
  }

  bindEvents() {
    this.querySelectorAll('[data-cookie-consent-accept]').forEach((button) => {
      button.addEventListener('click', () => this.acceptAll());
    });

    this.querySelectorAll('[data-cookie-consent-reject]').forEach((button) => {
      button.addEventListener('click', () => this.rejectOptional());
    });

    this.querySelector('[data-cookie-consent-manage]')?.addEventListener('click', () => this.openPanel());
    this.querySelector('[data-cookie-consent-save]')?.addEventListener('click', () => this.savePreferences());
    this.querySelector('[data-cookie-consent-close]')?.addEventListener('click', () => this.closePanel());
    this.backdrop?.addEventListener('click', () => this.closePanel());

    this.toggles.forEach((toggle) => {
      toggle.addEventListener('click', () => this.togglePreference(toggle));
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.panel?.hidden) {
        this.closePanel();
      }
    });

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-cookie-consent-open]');
      if (!trigger) return;
      event.preventDefault();
      this.show();
      this.openPanel();
    });
  }

  async loadPrivacyApi() {
    if (window.Shopify?.customerPrivacy) {
      return window.Shopify.customerPrivacy;
    }

    if (window.Shopify?.loadFeatures) {
      await new Promise((resolve) => {
        window.Shopify.loadFeatures([CONSENT_FEATURE], () => resolve());
      });
    }

    if (window.Shopify?.customerPrivacy) {
      return window.Shopify.customerPrivacy;
    }

    return new Promise((resolve) => {
      let attempts = 0;
      const timer = window.setInterval(() => {
        attempts += 1;
        if (window.Shopify?.customerPrivacy || attempts >= 20) {
          window.clearInterval(timer);
          resolve(window.Shopify?.customerPrivacy || null);
        }
      }, 100);
    });
  }

  shouldRender(api) {
    if (window.Shopify?.designMode || document.documentElement.classList.contains('shopify-design-mode')) {
      return true;
    }

    if (this.getStoredDecision()) {
      return false;
    }

    if (typeof api?.shouldShowBanner === 'function') {
      return api.shouldShowBanner();
    }

    return true;
  }

  show() {
    this.hidden = false;
    document.body.classList.add('cookie-consent-visible');
  }

  hide() {
    this.hidden = true;
    this.closePanel();
    document.body.classList.remove('cookie-consent-visible');
  }

  openPanel() {
    this.backdrop.hidden = false;
    this.panel.hidden = false;
    this.panel.querySelector('button')?.focus({ preventScroll: true });
  }

  closePanel() {
    if (this.backdrop) this.backdrop.hidden = true;
    if (this.panel) this.panel.hidden = true;
  }

  togglePreference(toggle) {
    const active = toggle.getAttribute('aria-pressed') !== 'true';
    toggle.setAttribute('aria-pressed', String(active));
    toggle.classList.toggle('is-on', active);
    toggle.textContent = active ? this.dataset.onLabel : this.dataset.offLabel;
  }

  acceptAll() {
    this.applyConsent({
      analytics: true,
      marketing: true,
      preferences: true,
      sale_of_data: true,
    });
  }

  rejectOptional() {
    this.applyConsent({
      analytics: false,
      marketing: false,
      preferences: false,
      sale_of_data: false,
    });
  }

  savePreferences() {
    const preferences = {
      analytics: false,
      marketing: false,
      preferences: false,
      sale_of_data: false,
    };

    this.toggles.forEach((toggle) => {
      preferences[toggle.dataset.cookieConsentToggle] = toggle.getAttribute('aria-pressed') === 'true';
    });

    this.applyConsent(preferences);
  }

  applyConsent(preferences) {
    const finish = () => {
      this.storeDecision(preferences);
      this.hide();
    };

    if (!this.privacyApi?.setTrackingConsent) {
      finish();
      return;
    }

    try {
      this.privacyApi.setTrackingConsent(preferences, () => finish());
    } catch (error) {
      const accepted = Object.values(preferences).some(Boolean);
      try {
        this.privacyApi.setTrackingConsent(accepted, () => finish());
      } catch (_) {
        finish();
      }
    }
  }

  getStoredDecision() {
    try {
      return window.localStorage.getItem(CONSENT_STORAGE_KEY);
    } catch (_) {
      return null;
    }
  }

  storeDecision(preferences) {
    if (window.Shopify?.designMode || document.documentElement.classList.contains('shopify-design-mode')) {
      return;
    }

    try {
      window.localStorage.setItem(
        CONSENT_STORAGE_KEY,
        JSON.stringify({
          preferences,
          createdAt: new Date().toISOString(),
        })
      );
    } catch (_) {}
  }
}

if (!customElements.get('cookie-consent')) {
  customElements.define('cookie-consent', CookieConsent);
}
